import { useState } from 'react';

import { StyledModal, CustomNotification } from 'common/components';
import AgGrid from 'common/components/ag-grid/AgGrid';

import * as endpoints from 'services/members/endpoints';
import { useGetColumnsGrid } from 'hooks/useGetColumns';
import { useCreateUserLocationFromCompany } from './hooks';

/**
 * Modal that renders the company location grid so the user can
 * pick one location to set as their profile location.
 *
 * @param {boolean}  props.isOpen      - Controls modal visibility
 * @param {Function} props.onClose     - Called when the modal is dismissed
 * @param {number}   props.memberId    - The company member ID to load locations for
 * @param {number}   props.userId      - The current user ID to save the location against
 * @param {Function} props.onSuccess   - Called after the location is successfully saved (e.g. refetch grid)
 */
const CompanyLocationPickerModal = ({
  isOpen,
  onClose,
  memberId,
  userId,
  onSuccess,
}) => {
  const [rowsSelected, setRowSelected] = useState([]);

  const contactLocationColumns = useGetColumnsGrid('member-locations');

  const { mutate: createFromCompany, isLoading } =
    useCreateUserLocationFromCompany({
      onSuccess: (response) => {
        if (response?.isSuccess) {
          CustomNotification.success('Location added to your profile');
          onSuccess?.();
        } else {
          CustomNotification.error(
            response?.message || 'Failed to add location'
          );
        }
        onClose();
      },
      onError: () => {
        CustomNotification.error('Failed to add location');
      },
    });

  const onSetSelectedRows = (selectedNodesDetail) => {
    setRowSelected(selectedNodesDetail);
  };

  const handleConfirm = () => {
    createFromCompany({
      userId,
      companyLocationId: rowsSelected[0]?.id,
    });
  };

  const selectedOneRow = rowsSelected?.length === 1;
  const containDeletedLocation = rowsSelected.find(
    (rowItem) => rowItem.status === 'Deleted'
  );
  const allowSetAsMyLocation = selectedOneRow && !containDeletedLocation;

  return (
    <StyledModal
      title='Add from Company'
      visible={isOpen}
      onCancel={onClose}
      width='70vw'
      maskClosable={false}
      destroyOnClose
      bodyStyle={{ height: 500 }}
      okButtonProps={{
        disabled: !allowSetAsMyLocation,
        loading: isLoading,
      }}
      okText='Set as My Location'
      onOk={handleConfirm}
    >
      <div style={{ height: '100%', overflowX: 'hidden' }}>
        <div style={{ minWidth: 800, height: 400 }}>
          <AgGrid
            columnDefs={contactLocationColumns}
            urlGrid={endpoints.MEMBER_LOCATIONS_ENDPOINT}
            urlGridDistinct={endpoints.MEMBER_COMPANY_LOCATION_GRID_DISTINCT}
            responseParams='data'
            paramsGrid={{ MemberId: memberId }}
            styleGrid={{ width: '100%', height: '100%' }}
            gridView={false}
            isShowItemSelectionLog={false}
            isCacheEditMode={false}
            onHandleSelectedRowCustom={onSetSelectedRows}
            rowClassRules={{ 'ag-hidden-row': 'true' }}
            gridConfigProps={{ rowSelection: 'single' }}
          />
        </div>
      </div>
    </StyledModal>
  );
};

export default CompanyLocationPickerModal;
